<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-va-intent-performance-header">
    <table border="0" class="list-header" cellspacing="0" cellpadding="0">
      <tr valign="absmiddle">
        <td width="215">
          <div class="wy-table-search">
            <div style="display: flex; gap: 8px">
              <div class="search-button material-icons" @click="handleSearch">search</div>
              <div style="display: flex;">
                <el-input ref="searchBarInput" placeholder="Search by intents" v-model="keyword" @keyup.native="handleKeypress" @change="inputChange" @keypress.enter.native="handleSearch">
                  <el-select v-if="searchFields" popper-class="wy-seardh-bar-select" v-model="dataModel.fieldName" slot="append" v-bind:style="{ width: selectTabWidth }">
                    <el-option v-for="field in searchFields" :label="field.label" :value="field.name" :key="field.name"></el-option>
                  </el-select>
                </el-input>
                <i class="fas fa-times-circle" v-if="keyword != ''" @click="clearKeywordSearch"></i>
              </div>

              <el-button :class="savedIntentsEnabled ? 'save-intent-btn-selected' : 'save-intent-btn'" @click="toggleSavedIntentFilter">
                <i class="save-intent-selected-svg"></i>
                <span>Saved</span>
              </el-button>
            </div>

            <wy-table-column class="el-dropdown submit-btn" ref="columnSelectionDropdown" :controller="controller" />
          </div>
        </td>
      </tr>
    </table>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import WyTableColumn from '../widgets/wy-table-column';
import WyUtils from '../../library/wy-utils.js';

export default {
  name: 'wy-va-intent-performance-header',
  props: ['controller', 'searchFields', 'callback'],
  data() {
    return {
      selectTabWidth: 0,
      keyword: '',
      searchTimeout: null,
    };
  },

  components: {
    'wy-table-column': WyTableColumn,
  },

  computed: {
    ...mapGetters(['dataModel', 'tableModel']),

    savedIntentsEnabled: {
      get() {
        return this.controller.savedIntentsEnabled;
      },
      set(value) {
        this.controller.savedIntentsEnabled = value;
      },
    },
  },
  methods: {
    isInputActive() {
      return this.dataModel.keyword != '';
    },

    handleSearch() {
      this.dataModel.keyword = this.keyword ? this.keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').toLowerCase() : '';
      if (this.callback) this.callback({ pageNumber: 1 });

      this.hightLightKeywords();
      this.updateExportButton();
    },
    hightLightKeywords() {
      this.tableModel.results.forEach((res) => {
        if (res.intent) {
          res.intent = res.intent.replace(/<span class='highlight-keywords'>|<\/span>/g, '');
        }
      });
      let search = this.dataModel.keyword;
      if (search !== '') {
        const regEx = new RegExp(search, 'gi');

        this.tableModel.results.forEach((res) => {
          res.intent = res.intent.replace(regEx, "<span class='highlight-keywords'>$&</span>");
        });
      }
    },

    handleIconClick() {
      if (this.isInputActive()) {
        this.dataModel.keyword = '';
        this.$refs.searchBarInput.$el.classList.remove('active');
      }
      this.handleSearch();
    },

    updateExportButton() {
      const savedTranscriptExists = this.tableModel && this.tableModel.results && this.tableModel.results.length > 0;
      const parsedKeyword = this.dataModel.keyword.replace(/[.*+?^${}()|[\]\\]/g, '').toLowerCase();

      if (savedTranscriptExists) {
        this.controller.disableExport = false
        if (this.tableModel.results.some((res) => res.intent.includes(parsedKeyword))) {
          this.controller.disableExport = false
        } else {
          this.controller.disableExport = true
        }
      } else {
        this.controller.disableExport = true
      }

    },

    async toggleSavedIntentFilter() {
      this.controller.savedIntentsEnabled = !this.controller.savedIntentsEnabled;
      this.controller.loadTables = true;
      await this.controller.fetchOptimizationIntentPerformanceSummary({ savedIntents: this.controller.savedIntentsEnabled, keyword: this.keyword });
      this.controller.applyGenerateTooltipMsg();
      this.controller.loadTables = false;


      const saveBtnEl = this.$el.querySelector('.save-intent-btn');
      if (saveBtnEl) {
        saveBtnEl.blur(); // to remove hover
      }

      this.updateExportButton();
    },

    clearKeywordSearch() {
      this.keyword = '';
      this.dataModel.keyword = '';
      this.handleSearch();
    },
    handleKeypress() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.dataModel.keyword = this.keyword ? this.keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').toLowerCase() : '';
        if (this.isInputActive()) {
          this.$refs.searchBarInput.$el.classList.add('active');
        } else {
          this.$refs.searchBarInput.$el.classList.remove('active');
        }
        this.hightLightKeywords();
        this.updateExportButton();
      }, 150);
    },

    inputChange() {
      if (this.keyword == '') {
        this.dataModel.keyword = this.keyword;
      }
    },
  },

  created() {
    this.keyword = this.dataModel.keyword;

    if (this.dataModel.fieldName === null && this.searchFields) {
      // get the default selected field
      let selectedField = this.searchFields[0];
      this.dataModel.fieldName = selectedField.name;
    }

    if (this.searchFields) {
      // get the dropdown item with longest text and calculate the with of the text
      let largestItem = this.searchFields.reduce((a, b) => (a.label.length > b.label.length ? a : b));
      this.selectTabWidth = WyUtils.calcTextWidth(largestItem.label, 14) + 55 + 'px';
    }
  },
  beforeDestroy() {
    this.dataModel.keyword = '';
  },
};
</script>

<style lang="scss">
.wy-va-intent-performance-header {
  .save-intent-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    width: 90px;
    height: 35px;
    font-weight: 400;
    color: var(--site-font-chart-color);
    &:hover {
      color: var(--site-font-chart-color);
      background-color: #f4f6f8;
      border: 1px solid #CAD2DA;
    }
  }
  .save-intent-btn-selected {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    width: 90px;
    height: 35px;
    font-weight: 400;
    color: var(--site-font-chart-color);
    border: 1px solid #106fbc;
    background-color: #e8f1ff;
  }

  .save-intent-svg {
    background-image: url('../../assets/icons/icon_grey_star.svg');
    width: fit-content;
    border: unset;
    background-size: 85%;
    background-repeat: no-repeat;
    padding: 10px;
    background-position-y: 10px;
    margin-right: 4px;
  }
  .save-intent-selected-svg {
    background-image: url('../../assets/icons/icon_golden_star.svg');
    width: fit-content;
    border: unset;
    background-size: 85%;
    background-repeat: no-repeat;
    padding: 10px;
    background-position-y: 9px;
    margin-right: 4px;
  }

  > table {
    width: 100%;
    .col3 {
      text-align: right;
    }
  }

  .list-header-group {
    display: inline-flex;
  }

  .wy-table-column .wy-table-column-dropdown .table-col-dropdown-text-show {
    display: inline-block;
  }
  .title {
    display: inline-block;
    color: var(--site-font-secondary-color);
    font-size: 25px;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-weight: 600;
  }

  .column-filter {
    height: 34px;
    display: inline-block;
  }
  .columns-setting-btn {
    background-repeat: no-repeat;
    background-color: white;
    width: 100px;
    height: 36px;
    display: flex;
    border: 1px solid #cad2da;
    border-radius: 4px;
    background-position-y: 7px;
    background-position-x: 10px;
    align-items: center;
    background-image: url('../../assets/icons/icon_setting.svg');

    span {
      display: none;
    }
  }

  .wy-table-column {
    text-align: right;
  }
  .table-page-title {
    font-size: 20px;
    color: var(--site-font-secondary-color);
    // margin-left: 22px;
  }
  .wy-table-search {
    border: 0px solid #cad2da;
    background: #fff;
    padding: 0px 0px 20px 0px;
    display: flex;
    height: 30px;
    border-bottom: unset;
    justify-content: space-between;
    align-items: center;
    .intent-performance-header-flex {
      display: flex;
      width: 592px;
      position: relative;
      left: 20px;
      align-items: center;
      justify-content: space-around;
    }
    .el-input__icon.el-icon-click {
      background-size: 15px 20px;
      background-position-y: 3px;
      background-position-x: 5px;
    }
    div.el-input,
    .wy-table-search .el-input-group {
      width: 200px;
    }
  }
  div.el-input,
  .el-input-group {
    background: #ffffff 0% 0% no-repeat padding-box;
    border: 1px solid #d7d7d8;
    border-radius: 4px;
    opacity: 1;
    width: 258px !important;
    margin-left: 0px;
    padding-left: 12px;
    height: 33px;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    padding-right: 50px;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    &:focus-within {
      border: 1px solid #106fbc !important;
      background-color: #fff;
    }
  }

  .el-icon-circle-close:before {
    content: '\E79D';
    color: #acbdce;
  }

  .search-button {
    border-radius: 8px;
    width: 38px;
    height: 23px;
    text-align: center;
    margin-top: 0px;
    position: relative;
    left: 301px;
    padding: 6px 0px 0px 0px;
    font-size: 22px;
    position: absolute;
    z-index: 1;
    background: transparent;
    color: #5c5f62;
    cursor: pointer;
  }

  input {
    border: none;
    padding-left: 0px;
    height: 30px !important;
    color: var(--site-font-tertiary-color);
    background-color: transparent;
    font-size: 14.22px;

    &:hover {
      background: #f4f6f8;
    }
    &:focus-within {
      background: #fff;
    }
  }
  input::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: #4b5158;
    opacity: 1; /* Firefox */
  }
  input:-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    color: #4b5158;
  }

  input::-ms-input-placeholder {
    /* Microsoft Edge */
    color: #4b5158;
  }
  .el-input-group__append {
    border: none;
    background-color: #fff;
    border-left: 1px solid #d7d7d8 !important;
  }
  .el-input__icon {
    line-height: 32px;
  }
  .el-input__icon.el-icon-click {
    background-image: url('../../assets/icons/icon_search.svg');
    background-repeat: no-repeat;
    background-size: 20px 28px;
    background-position-y: 1px;
    background-position-x: 5px;
  }
  .el-input__icon.el-icon-click:hover {
    cursor: pointer;
  }
  .el-input__icon.el-icon-click::before {
    content: '';
  }
  .el-input__icon.el-icon-click {
    content: '';
  }

  .el-select {
    .el-input__inner {
      color: #4b5158;
    }
  }
  .el-select__caret {
    background-image: url('../../assets/icons/icon_dropdown_down.svg');
    background-repeat: no-repeat;
    background-size: 12px 9px;
    background-position-y: 16px;
    background-position-x: 3px;
    transform: none;
  }
  .el-select__caret::before {
    content: '';
  }
  .el-select__caret::after {
    content: '';
  }
  .fa-times-circle {
    position: relative;
    top: 8px;
    left: -53px;
    margin-right: -20px;
    float: right;
    color: #5c5f62 !important;
    font-size: 20px;
    cursor: pointer;
  }
}
.el-input-group.active {
  .el-input__inner {
    color: var(--site-font-primary-color);
  }
  .el-select__caret {
    background-image: url('../../assets/icons/icon_dropdown_blue_down.svg');
  }
  .el-input__icon.el-icon-click {
    background-image: url('../../assets/icons/icon_delete.svg') !important;
  }
}

.wy-seardh-bar-select {
  .el-select-dropdown__item.selected {
    color: var(--site-font-primary-color) !important;
  }
}
</style>
